import { ApolloError, ApolloQueryResult, FetchPolicy, useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { useCurrentUserId } from './useCurrentUserId';
import { useIsExternal } from './useIsExternal';

interface UseProfileProps {
  fetchPolicy?: FetchPolicy;
  skip?: boolean;
}

interface UseProfileResult {
  profile: API.ProfileFragment | undefined;
  refetch: () => Promise<ApolloQueryResult<API.GetCurrentUserQuery>>;
  error?: ApolloError;
  loading: boolean;
}

const defaultProps: UseProfileProps = {
  fetchPolicy: 'cache-first',
  skip: false,
};

export function useProfile({
  fetchPolicy,
  skip,
}: UseProfileProps = defaultProps): UseProfileResult {
  const currentUserId = useCurrentUserId();
  // External/public forms carry an anonymous user id but have no real profile to
  // fetch — skip the getCurrentUser query there to avoid an unnecessary request.
  const isExternal = useIsExternal();
  const { data, refetch, error, loading } = useQuery<
    API.GetCurrentUserQuery,
    API.GetCurrentUserQueryVariables
  >(Queries.user.getCurrentUser, {
    variables: { id: currentUserId ?? -1 },
    fetchPolicy,
    skip: skip || isExternal || isNil(currentUserId),
  });

  return useMemo(() => {
    return {
      profile: data?.user ?? undefined,
      refetch,
      error,
      loading,
    };
  }, [data, refetch, error, loading]);
}
